import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Copy, Rocket, ChevronRight, ChevronLeft } from "lucide-react";
import i18n from "@/i18n";

// ─────────────────────────────────────────────────────────────
// Customer Provisioning Wizard — Prompt 30
// 6-step guided flow that atomically creates a company, its trial
// subscription, its first restaurant, a primary user and an optional
// head chef. Backend endpoint: POST /api/admin/customers/provision.
// Temporary passwords are shown once on the final screen; the plain
// text is never stored server-side.
// ─────────────────────────────────────────────────────────────

const SUBSCRIPTION_STATUSES = [
    "trial", "active", "pending_payment", "suspended", "manual_invoice",
];

const PRIMARY_ROLES = [
    { value: "corporate",         get label() { return i18n.t("admin.l.Corporate_company_level", "Corporate (company-level)"); } },
    { value: "restaurant_admin",  get label() { return i18n.t("admin.l.Restaurant_Admin", "Restaurant Admin"); } },
    { value: "head_chef",         get label() { return i18n.t("admin.l.Head_Chef", "Head Chef"); } },
];

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400_000).toISOString().slice(0, 10);

const emptyState = () => ({
    company: {
        name: "", country: "", vat_number: "",
        contact_person: "", contact_email: "", phone: "",
        internal_notes: "",
    },
    subscription: {
        plan: "trial", status: "trial",
        trial_start: today(), trial_end: plusDays(30),
        billing_email: "", notes: "",
    },
    restaurant: {
        name: "", city: "", country: "",
        time_zone: "Europe/Helsinki", currency: "EUR",
        language: "en", address: "", active: true,
    },
    primary_user: {
        name: "", email: "", role: "corporate",
        // Optional manual password — Super Admin owns password lifecycle.
        password: "", password_confirm: "",
    },
    head_chef: {
        enabled: false, name: "", email: "",
        password: "", password_confirm: "",
    },
});

export default function CustomerProvisioningWizard({ onClose, onCreated }) {
    const [step, setStep] = useState(1);
    const [state, setState] = useState(emptyState);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const upd = (section, patch) =>
        setState((s) => ({ ...s, [section]: { ...s[section], ...patch } }));

    // Guard fields per step so the "Next" button is only enabled when the
    // minimum required data is present. This keeps the flow forgiving —
    // truly optional fields are truly optional.
    const canAdvance = () => {
        if (step === 1) {
            return state.company.name.trim().length > 1
                && state.company.contact_person.trim().length > 1
                && state.company.contact_email.includes("@");
        }
        if (step === 2) {
            return state.subscription.plan
                && SUBSCRIPTION_STATUSES.includes(state.subscription.status);
        }
        if (step === 3) {
            return state.restaurant.name.trim().length > 1
                && state.restaurant.city.trim().length > 1;
        }
        if (step === 4) {
            const u = state.primary_user;
            const nameEmailOk = u.name.trim().length > 1
                && u.email.includes("@")
                && PRIMARY_ROLES.some((r) => r.value === u.role);
            // If admin typed a password, both fields must match & be ≥6 chars.
            const pwdOk = !u.password
                ? true
                : (u.password.length >= 6 && u.password === u.password_confirm);
            return nameEmailOk && pwdOk;
        }
        if (step === 5) {
            if (!state.head_chef.enabled) return true;
            const c = state.head_chef;
            const nameEmailOk = c.name.trim().length > 1 && c.email.includes("@");
            const pwdOk = !c.password
                ? true
                : (c.password.length >= 6 && c.password === c.password_confirm);
            return nameEmailOk && pwdOk;
        }
        return true;
    };

    const create = async () => {
        setBusy(true);
        try {
            const payload = {
                company: state.company,
                subscription: state.subscription,
                restaurant: state.restaurant,
                primary_user: {
                    name: state.primary_user.name,
                    email: state.primary_user.email,
                    role: state.primary_user.role,
                    password: state.primary_user.password || null,
                },
                head_chef: state.head_chef.enabled
                    ? {
                        name: state.head_chef.name,
                        email: state.head_chef.email,
                        password: state.head_chef.password || null,
                    }
                    : null,
            };
            const { data } = await api.post("/admin/customers/provision", payload);
            setResult(data);
            toast.success(i18n.t("admin.l.Customer_created_successfully", "Customer created successfully."));
        } catch (e) {
            toast.error(apiErrorMessage(e) || i18n.t("admin.l.Failed_to_create_customer", "Failed to create customer"));
        } finally { setBusy(false); }
    };

    const close = () => {
        // If we already created, notify the parent to refresh the Companies tab.
        if (result && onCreated) onCreated();
        else if (onClose) onClose();
    };

    // ── Success screen ─────────────────────────────────────────
    if (result) {
        return (
            <Dialog open onOpenChange={close}>
                <DialogContent className="max-w-2xl" data-testid="wizard-success">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            {i18n.t("admin.customerCreatedTitle", "Customer created successfully")}
                        </DialogTitle>
                        <DialogDescription>
                            {i18n.t("admin.l.Company_created_Restaurant_created_Users_created_Trial_activated", "Company created. Restaurant created. Users created. Trial activated.")}
                            {i18n.t("admin.l.Onboarding_enabled", "Onboarding enabled.")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <SummaryLine label={i18n.t("admin.l.Company", "Company")}      value={result.company?.name} />
                        <SummaryLine label={i18n.t("admin.l.Plan_Trial", "Plan / Trial")} value={`${result.subscription?.plan} · ${result.subscription?.status}`} />
                        <SummaryLine label={i18n.t("admin.l.Restaurant", "Restaurant")}   value={`${result.restaurant?.name} · ${result.restaurant?.city}`} />
                        <TempPasswordCard label="Primary user" user={result.primary_user} />
                        {result.head_chef && (
                            <TempPasswordCard label="Head chef" user={result.head_chef} />
                        )}
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            {i18n.t("admin.l.Save_these_temporary_passwords_now_they_will_not_be_shown_again", "Save these temporary passwords now — they will not be shown again.")}
                            {i18n.t("admin.l.The_users_will_be_asked_to_change_them_on_first_login", "The users will be asked to change them on first login.")}
                        </div>
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            data-testid="wizard-close"
                            onClick={close}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                            Done
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // ── Steps ──────────────────────────────────────────────────
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="customer-wizard">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Rocket className="h-5 w-5" /> New Customer — Step {step} of 6
                    </DialogTitle>
                    <DialogDescription>
                        {STEP_TITLES[step - 1]()}
                    </DialogDescription>
                </DialogHeader>

                <StepDots step={step} />

                <div className="mt-4 space-y-4">
                    {step === 1 && <Step1 state={state.company} upd={(p) => upd("company", p)} />}
                    {step === 2 && <Step2 state={state.subscription} upd={(p) => upd("subscription", p)} />}
                    {step === 3 && <Step3 state={state.restaurant} upd={(p) => upd("restaurant", p)} />}
                    {step === 4 && <Step4 state={state.primary_user} upd={(p) => upd("primary_user", p)} />}
                    {step === 5 && <Step5 state={state.head_chef} upd={(p) => upd("head_chef", p)} />}
                    {step === 6 && <Step6 state={state} />}
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                    <button
                        data-testid="wizard-back"
                        onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm inline-flex items-center gap-1">
                        <ChevronLeft className="h-4 w-4" /> {step > 1 ? i18n.t("admin.l.Back", "Back") : i18n.t("admin.l.Cancel", "Cancel")}
                    </button>
                    {step < 6 ? (
                        <button
                            data-testid="wizard-next"
                            disabled={!canAdvance()}
                            onClick={() => setStep(step + 1)}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <button
                            data-testid="wizard-create"
                            disabled={busy}
                            onClick={create}
                            className="h-10 px-4 rounded-md bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                            {busy ? i18n.t("admin.l.Creating", "Creating…") : i18n.t("admin.l.Create_Customer", "Create Customer")}
                        </button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

const STEP_TITLES = [
    () => i18n.t("admin.step.1", "Company — the legal customer entity."),
    () => i18n.t("admin.step.2", "Subscription & Trial — plan and trial window."),
    () => i18n.t("admin.step.3", "First Restaurant — where the kitchen operates."),
    () => i18n.t("admin.step.4", "Primary User — the person who will log in first."),
    () => i18n.t("admin.step.5", "Head Chef (optional) — a second account for the kitchen lead."),
    () => i18n.t("admin.step.6", "Review & Create — confirm and provision the customer."),
];

function StepDots({ step }) {
    return (
        <div className="flex items-center gap-1.5 mt-2" data-testid="wizard-step-dots">
            {[1, 2, 3, 4, 5, 6].map((n) => (
                <span
                    key={n}
                    data-testid={`wizard-dot-${n}${n === step ? "-active" : ""}`}
                    className={`h-1.5 rounded-full transition-all ${
                        n === step ? "bg-neutral-900 w-8" :
                        n < step ? "bg-neutral-500 w-4" : "bg-neutral-200 w-4"
                    }`}
                />
            ))}
        </div>
    );
}

function Field({ label, required, children, hint, testId }) {
    return (
        <div data-testid={testId}>
            <label className="text-xs font-semibold text-neutral-600">
                {label} {required && <span className="text-rose-600">*</span>}
            </label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[11px] text-neutral-500 mt-0.5">{hint}</div>}
        </div>
    );
}

const inputCls = "h-10 w-full px-3 rounded-md border border-neutral-300 text-sm focus:border-neutral-900 focus:outline-none";

function Step1({ state, upd }) {
    return (
        <div className="grid gap-3 md:grid-cols-2">
            <Field label={i18n.t("admin.l.Company_name", "Company name")} required testId="wf-company-name-field">
                <input data-testid="wf-company-name" className={inputCls}
                    value={state.name} onChange={(e) => upd({ name: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Country", "Country")} testId="wf-company-country-field">
                <input data-testid="wf-company-country" className={inputCls}
                    value={state.country} onChange={(e) => upd({ country: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.VAT_number", "VAT number")} testId="wf-company-vat-field">
                <input data-testid="wf-company-vat" className={inputCls}
                    value={state.vat_number} onChange={(e) => upd({ vat_number: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Contact_person", "Contact person")} required testId="wf-company-contact-field">
                <input data-testid="wf-company-contact" className={inputCls}
                    value={state.contact_person} onChange={(e) => upd({ contact_person: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Contact_email", "Contact email")} required testId="wf-company-email-field">
                <input data-testid="wf-company-email" type="email" className={inputCls}
                    value={state.contact_email} onChange={(e) => upd({ contact_email: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Phone", "Phone")} testId="wf-company-phone-field">
                <input data-testid="wf-company-phone" className={inputCls}
                    value={state.phone} onChange={(e) => upd({ phone: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
                <Field label={i18n.t("admin.l.Internal_notes_private_to_platform_team", "Internal notes (private to platform team)")} testId="wf-company-notes-field">
                    <textarea data-testid="wf-company-notes" rows={2}
                        className={`${inputCls} h-auto py-2`}
                        value={state.internal_notes}
                        onChange={(e) => upd({ internal_notes: e.target.value })} />
                </Field>
            </div>
        </div>
    );
}

function Step2({ state, upd }) {
    return (
        <div className="grid gap-3 md:grid-cols-2">
            <Field label={i18n.t("admin.l.Plan", "Plan")} required testId="wf-sub-plan-field">
                <input data-testid="wf-sub-plan" className={inputCls}
                    value={state.plan}
                    placeholder={i18n.t("admin.planPlaceholder", "trial · starter · professional · business")}
                    onChange={(e) => upd({ plan: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Subscription_status", "Subscription status")} required testId="wf-sub-status-field">
                <select data-testid="wf-sub-status" className={inputCls}
                    value={state.status} onChange={(e) => upd({ status: e.target.value })}>
                    {SUBSCRIPTION_STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    ))}
                </select>
            </Field>
            <Field label={i18n.t("admin.l.Trial_start", "Trial start")} testId="wf-sub-trial-start-field">
                <input data-testid="wf-sub-trial-start" type="date" className={inputCls}
                    value={state.trial_start || ""} onChange={(e) => upd({ trial_start: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Trial_end", "Trial end")} testId="wf-sub-trial-end-field">
                <input data-testid="wf-sub-trial-end" type="date" className={inputCls}
                    value={state.trial_end || ""} onChange={(e) => upd({ trial_end: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Billing_email", "Billing email")} testId="wf-sub-billing-email-field">
                <input data-testid="wf-sub-billing-email" type="email" className={inputCls}
                    value={state.billing_email} onChange={(e) => upd({ billing_email: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
                <Field label={i18n.t("admin.l.Notes", "Notes")} testId="wf-sub-notes-field">
                    <textarea data-testid="wf-sub-notes" rows={2}
                        className={`${inputCls} h-auto py-2`}
                        value={state.notes} onChange={(e) => upd({ notes: e.target.value })} />
                </Field>
            </div>
        </div>
    );
}

function Step3({ state, upd }) {
    return (
        <div className="grid gap-3 md:grid-cols-2">
            <Field label={i18n.t("admin.l.Restaurant_name", "Restaurant name")} required testId="wf-rest-name-field">
                <input data-testid="wf-rest-name" className={inputCls}
                    value={state.name} onChange={(e) => upd({ name: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.City", "City")} required testId="wf-rest-city-field">
                <input data-testid="wf-rest-city" className={inputCls}
                    value={state.city} onChange={(e) => upd({ city: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Country", "Country")} testId="wf-rest-country-field">
                <input data-testid="wf-rest-country" className={inputCls}
                    value={state.country} onChange={(e) => upd({ country: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Time_zone", "Time zone")} testId="wf-rest-tz-field">
                <input data-testid="wf-rest-tz" className={inputCls}
                    value={state.time_zone} onChange={(e) => upd({ time_zone: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Currency", "Currency")} testId="wf-rest-currency-field">
                <input data-testid="wf-rest-currency" className={inputCls}
                    value={state.currency} onChange={(e) => upd({ currency: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Language", "Language")} testId="wf-rest-language-field">
                <input data-testid="wf-rest-language" className={inputCls}
                    value={state.language} onChange={(e) => upd({ language: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
                <Field label={i18n.t("admin.l.Address", "Address")} testId="wf-rest-address-field">
                    <input data-testid="wf-rest-address" className={inputCls}
                        value={state.address} onChange={(e) => upd({ address: e.target.value })} />
                </Field>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
                <input id="wf-rest-active" data-testid="wf-rest-active"
                    type="checkbox" checked={state.active}
                    onChange={(e) => upd({ active: e.target.checked })} />
                <label htmlFor="wf-rest-active" className="text-sm">
                    {i18n.t("admin.l.Active_on_creation", "Active on creation")}
                </label>
            </div>
        </div>
    );
}

function Step4({ state, upd }) {
    return (
        <div className="grid gap-3 md:grid-cols-2">
            <Field label={i18n.t("admin.l.Full_name", "Full name")} required testId="wf-user-name-field">
                <input data-testid="wf-user-name" className={inputCls}
                    value={state.name} onChange={(e) => upd({ name: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Email", "Email")} required testId="wf-user-email-field">
                <input data-testid="wf-user-email" type="email" className={inputCls}
                    value={state.email} onChange={(e) => upd({ email: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
                <Field label={i18n.t("admin.l.Role", "Role")} required testId="wf-user-role-field"
                       hint={i18n.t("admin.roleHint", "Corporate can manage every restaurant of this company. Restaurant Admin / Head Chef are scoped to one restaurant.")}>
                    <select data-testid="wf-user-role" className={inputCls}
                        value={state.role} onChange={(e) => upd({ role: e.target.value })}>
                        {PRIMARY_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </Field>
            </div>
            <PasswordFields state={state} upd={upd} idPrefix="wf-user" />
            <div className="md:col-span-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
                {i18n.t("admin.pwEmptyHint", "Leave the password fields empty to auto-generate a temporary one — it will be shown once after creation.")}
            </div>
        </div>
    );
}

function Step5({ state, upd }) {
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <input id="hc-enable" data-testid="wf-hc-enable"
                    type="checkbox" checked={state.enabled}
                    onChange={(e) => upd({ enabled: e.target.checked })} />
                <label htmlFor="hc-enable" className="text-sm font-semibold">
                    {i18n.t("admin.l.Also_create_a_Head_Chef_account", "Also create a Head Chef account")}
                </label>
            </div>
            {state.enabled && (
                <div className="grid gap-3 md:grid-cols-2">
                    <Field label={i18n.t("admin.l.Full_name", "Full name")} required testId="wf-hc-name-field">
                        <input data-testid="wf-hc-name" className={inputCls}
                            value={state.name} onChange={(e) => upd({ name: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("admin.l.Email", "Email")} required testId="wf-hc-email-field">
                        <input data-testid="wf-hc-email" type="email" className={inputCls}
                            value={state.email} onChange={(e) => upd({ email: e.target.value })} />
                    </Field>
                    <PasswordFields state={state} upd={upd} idPrefix="wf-hc" />
                    <div className="md:col-span-2 text-xs text-neutral-500">
                        {i18n.t("admin.l.The_head_chef_is_automatically_assigned_to_the_restaurant_created_abov", "The head chef is automatically assigned to the restaurant created above.")}
                    </div>
                </div>
            )}
            {!state.enabled && (
                <p className="text-sm text-neutral-500">
                    {i18n.t("admin.skipStep", "You can skip this step. Additional users can be added later from the company detail page.")}
                </p>
            )}
        </div>
    );
}

// MiseOne Password Policy — password management belongs to Super
// Admin. Users are NOT forced to change their password on first login.
function PasswordFields({ state, upd, idPrefix }) {
    const mismatch = state.password && state.password_confirm && state.password !== state.password_confirm;
    return (
        <>
            <Field label={i18n.t("admin.l.Password", "Password")} testId={`${idPrefix}-password-field`}
                   hint={i18n.t("admin.l.Optional_leave_empty_to_auto_generate_a_temporary_password", "Optional — leave empty to auto-generate a temporary password.")}>
                <input data-testid={`${idPrefix}-password`} type="password"
                    className={inputCls}
                    value={state.password || ""}
                    onChange={(e) => upd({ password: e.target.value })} />
            </Field>
            <Field label={i18n.t("admin.l.Confirm_Password", "Confirm Password")} testId={`${idPrefix}-password-confirm-field`}
                   hint={mismatch ? i18n.t("admin.l.Passwords_do_not_match", "Passwords do not match.") : i18n.t("admin.l.Retype_the_password", "Retype the password.")}>
                <input data-testid={`${idPrefix}-password-confirm`} type="password"
                    className={`${inputCls} ${mismatch ? "border-rose-400" : ""}`}
                    value={state.password_confirm || ""}
                    onChange={(e) => upd({ password_confirm: e.target.value })} />
            </Field>
        </>
    );
}

function Step6({ state }) {
    return (
        <div className="space-y-3" data-testid="wf-review">
            <ReviewRow k={i18n.t("admin.l.Company", "Company")} v={state.company.name}
                sub={[state.company.country, state.company.contact_person].filter(Boolean).join(" · ")} />
            <ReviewRow k={i18n.t("admin.l.Plan_Trial", "Plan / Trial")}
                v={`${state.subscription.plan} · ${state.subscription.status}`}
                sub={`${state.subscription.trial_start} → ${state.subscription.trial_end}`} />
            <ReviewRow k={i18n.t("admin.l.Restaurant", "Restaurant")} v={state.restaurant.name}
                sub={`${state.restaurant.city} · ${state.restaurant.time_zone}`} />
            <ReviewRow k={i18n.t("admin.primaryUser", "Primary user")}
                v={`${state.primary_user.name} <${state.primary_user.email}>`}
                sub={PRIMARY_ROLES.find((r) => r.value === state.primary_user.role)?.label} />
            {state.head_chef.enabled && (
                <ReviewRow k={i18n.t("admin.headChef", "Head chef")}
                    v={`${state.head_chef.name} <${state.head_chef.email}>`}
                    sub={i18n.t("admin.l.Assigned_to_the_restaurant_above", "Assigned to the restaurant above")} />
            )}
        </div>
    );
}

function ReviewRow({ k, v, sub }) {
    return (
        <div className="flex items-baseline justify-between border-b border-neutral-100 py-2 gap-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 min-w-[110px]">
                {k}
            </div>
            <div className="flex-1 text-right">
                <div className="text-sm font-semibold">{v || <span className="italic text-neutral-400">—</span>}</div>
                {sub && <div className="text-xs text-neutral-500">{sub}</div>}
            </div>
        </div>
    );
}

function SummaryLine({ label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-neutral-100 py-2">
            <span className="text-xs uppercase tracking-wide text-neutral-500">{label}</span>
            <span className="font-semibold text-sm">{value}</span>
        </div>
    );
}

function TempPasswordCard({ label, user }) {
    if (!user) return null;
    // Prompt 33: if the admin set a password manually the backend returns
    // temporary_password=null. We show a subtle confirmation instead of
    // the plaintext so the admin's chosen password stays private.
    const isAdminSet = user.temporary_password == null;
    const copy = () => {
        if (isAdminSet) return;
        navigator.clipboard.writeText(user.temporary_password);
        toast.success(i18n.t("admin.l.Copied_temporary_password", "Copied temporary password"));
    };
    return (
        <div className="rounded-md border border-neutral-200 bg-white p-3"
             data-testid={`wf-temp-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
            <div className="text-sm font-semibold">{user.name} · {user.email}</div>
            {isAdminSet ? (
                <div className="mt-2 rounded-md bg-neutral-100 text-neutral-600 px-3 py-2 text-xs italic"
                     data-testid={`wf-admin-set-${label.toLowerCase().replace(/\s/g, "-")}`}>
                    {i18n.t("admin.l.Password_set_manually_by_admin_not_shown_here", "Password set manually by admin — not shown here.")}
                </div>
            ) : (
                <div className="mt-2 flex items-center justify-between rounded-md bg-neutral-900 text-white px-3 py-2 font-mono text-sm">
                    <span>{user.temporary_password}</span>
                    <button onClick={copy}
                        className="ml-3 h-7 w-7 grid place-items-center rounded hover:bg-neutral-800"
                        title={i18n.t("admin.l.Copy", "Copy")}>
                        <Copy className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
